import { ROUTE_PATHS } from '../../routes/path-map';

/**
 * Workspace nav — prototype 09b / TV_SAAS.workspaceNav (Create · Library · Insights · Account · Admin).
 * Each item's `roles` array controls visibility — must stay in sync with route guards.
 */
export const SIDEBAR_SECTIONS = [
  {
    id: 'create',
    titleKey: 'Create',
    showSectionHeader: true,
    items: [
      {
        id: 'dashboard',
        to: '/dashboard',
        icon: 'bx bx-home-circle',
        labelKey: 'Dashboard',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'campaigns',
        to: '/campaigns',
        icon: 'bx bx-analyse',
        labelKey: 'Campaigns',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'templates',
        to: '/templates',
        icon: 'bx bx-collection',
        labelKey: 'Templates',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'editor',
        action: 'createDesign',
        icon: 'bx bx-pencil',
        labelKey: 'Editor',
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'workflows',
        to: ROUTE_PATHS.workflows,
        icon: 'bx bx-git-branch',
        labelKey: 'Workflows',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
        featureKey: 'workflows',
      },
    ],
  },
  {
    id: 'library',
    titleKey: 'Library',
    showSectionHeader: true,
    items: [
      {
        id: 'assets',
        to: '/assets',
        icon: 'bx bx-image',
        labelKey: 'Asset Library',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'tag-manager',
        to: ROUTE_PATHS.manageTags,
        icon: 'bx bx-purchase-tag',
        labelKey: 'Tag Manager',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
      },
      {
        id: 'actors',
        to: '/actors',
        icon: 'bx bx-user',
        labelKey: 'Actors',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'crm',
        to: '/crm',
        icon: 'bx bx-group',
        labelKey: 'CRM & Shopify',
        crownTitle: 'CRM — Pay as you go',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        featureKey: 'shopify',
        premiumLocked: true,
        freeHidden: false,
      },
      {
        id: 'trustvid-crm',
        to: ROUTE_PATHS.trustvidCrm,
        icon: 'bx bx-briefcase',
        labelKey: 'TrustVid CRM',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        featureKey: 'trustvid_crm',
        freeHidden: true,
      },
    ],
  },
  {
    id: 'insights',
    titleKey: 'Insights',
    showSectionHeader: true,
    items: [
      {
        id: 'video-analytics',
        to: '/video-analytics',
        icon: 'bx bx-pie-chart-alt',
        labelKey: 'Video Analytics',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        premiumLocked: true,
      },
      {
        id: 'metering',
        to: '/metering',
        icon: 'bx bx-bar-chart-alt-2',
        labelKey: 'Usage & metering',
        crownTitle: 'Metering — Pay as you go',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        premiumLocked: true,
      },
    ],
  },
  {
    id: 'account',
    titleKey: 'Account',
    showSectionHeader: true,
    items: [
      {
        id: 'billing',
        to: ROUTE_PATHS.billing,
        icon: 'bx bx-wallet',
        labelKey: 'Billing',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
      },
      {
        id: 'starter',
        to: ROUTE_PATHS.starterHome,
        icon: 'bx bx-flag',
        labelKey: 'Starter guide',
        scoped: true,
        roles: ['SuperAdmin', 'Admin', 'User'],
        freeOnly: true,
      },
    ],
  },
  {
    id: 'admin',
    titleKey: 'Admin',
    showSectionHeader: true,
    items: [
      {
        id: 'users',
        to: '/users',
        icon: 'bx bx-user-circle',
        labelKey: 'Team',
        scoped: true,
        roles: ['SuperAdmin', 'Admin'],
        adminBadge: true,
        freeHidden: true,
      },
      {
        id: 'video-pipeline',
        to: ROUTE_PATHS.videoPipeline,
        icon: 'bx bx-film',
        labelKey: 'Video Pipeline',
        scoped: true,
        roles: ['SuperAdmin'],
        adminBadge: true,
      },
    ],
  },
];

/**
 * @param {string} role
 * @param {string} [appEnv]
 * @param {string[]} [enabledFeatureKeys] lowercase feature keys that are on for the account
 * @param {{ plan?: string, isPremium?: boolean }} [saasPlan]
 * @returns {Array<{ id: string, titleKey: string, items: object[] }>}
 */
export function getVisibleSections(
  role,
  appEnv = process.env.REACT_APP_ENV || 'dev',
  enabledFeatureKeys = [],
  saasPlan = {}
) {
  const enabled = new Set(
    (enabledFeatureKeys || []).map((k) => String(k || '').toLowerCase()).filter(Boolean)
  );
  const isFree =
    !saasPlan?.isPremium &&
    String(saasPlan?.plan || 'free').toLowerCase() === 'free';
  return SIDEBAR_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (!item.roles.includes(role)) {
        return false;
      }
      if (item.envs && !item.envs.includes(appEnv)) {
        return false;
      }
      if (item.featureKey && !enabled.has(String(item.featureKey).toLowerCase())) {
        return false;
      }
      if (isFree && item.freeHidden) {
        return false;
      }
      if (item.freeOnly && !isFree) {
        return false;
      }
      return true;
    }),
  })).filter((section) => section.items.length > 0);
}
